import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

// Tips organizados por paso/contexto
const CONTEXTUAL_TIPS = {
  // Tips para subida de documentos
  documents: {
    general: [
      {
        id: "doc-1",
        title: "Calidad de imagen",
        tip: "Asegúrate de que todos los documentos sean legibles. Usa buena iluminación y evita sombras.",
        icon: "📸"
      },
      {
        id: "doc-2",
        title: "Formato correcto",
        tip: "Sube archivos en PDF, JPG o PNG. El tamaño máximo es 10MB por archivo.",
        icon: "📁"
      },
      {
        id: "doc-3",
        title: "Documentos vigentes",
        tip: "Tu pasaporte debe tener al menos 6 meses de vigencia después de tu fecha de viaje.",
        icon: "📅"
      }
    ],
    passport: [
      {
        id: "passport-1",
        title: "Página de datos",
        tip: "Sube la página donde aparece tu foto, nombre y número de pasaporte.",
        icon: "🛂"
      },
      {
        id: "passport-2",
        title: "Sin reflejos",
        tip: "Evita el flash directo que pueda causar reflejos en la página plastificada.",
        icon: "✨"
      }
    ],
    photo: [
      {
        id: "photo-1",
        title: "Fondo blanco",
        tip: "La foto debe tener fondo completamente blanco, sin sombras ni texturas.",
        icon: "⬜"
      },
      {
        id: "photo-2",
        title: "Medidas exactas",
        tip: "La foto debe ser 5x5 cm (2x2 pulgadas). Tu cara debe ocupar el 70% de la imagen.",
        icon: "📐"
      },
      {
        id: "photo-3",
        title: "Expresión neutral",
        tip: "Mira directamente a la cámara con expresión neutral y boca cerrada.",
        icon: "😐"
      },
      {
        id: "photo-4",
        title: "Sin accesorios",
        tip: "No uses lentes, gorras ni accesorios que cubran tu rostro (excepto por motivos religiosos).",
        icon: "🚫"
      }
    ],
    bank_statement: [
      {
        id: "bank-1",
        title: "Últimos 3 meses",
        tip: "El estado de cuenta debe mostrar los últimos 3 meses de movimientos.",
        icon: "🏦"
      },
      {
        id: "bank-2",
        title: "Saldo suficiente",
        tip: "Demuestra solvencia económica para cubrir tu viaje y estancia.",
        icon: "💰"
      }
    ],
    work_letter: [
      {
        id: "work-1",
        title: "Membrete oficial",
        tip: "La carta debe estar en papel membretado de tu empresa con datos de contacto.",
        icon: "📝"
      },
      {
        id: "work-2",
        title: "Información completa",
        tip: "Incluye: cargo, antigüedad, salario y fecha de regreso autorizada.",
        icon: "✅"
      }
    ]
  },
  
  // Tips para DS-160
  ds160: {
    general: [
      {
        id: "ds160-1",
        title: "Consistencia",
        tip: "Toda la información debe coincidir EXACTAMENTE con tu pasaporte y otros documentos.",
        icon: "🔍"
      },
      {
        id: "ds160-2",
        title: "Guarda frecuentemente",
        tip: "El sistema puede expirar. Guarda tu progreso cada 5-10 minutos.",
        icon: "💾"
      },
      {
        id: "ds160-3",
        title: "En inglés",
        tip: "Todo el formulario debe completarse en INGLÉS (excepto tu nombre en caracteres nativos).",
        icon: "🇺🇸"
      }
    ],
    personal: [
      {
        id: "personal-1",
        title: "Nombre exacto",
        tip: "Escribe tu nombre EXACTAMENTE como aparece en tu pasaporte, incluyendo todos los nombres.",
        icon: "📛"
      },
      {
        id: "personal-2",
        title: "Fechas en formato US",
        tip: "Usa el formato de fecha americano: MES/DÍA/AÑO (MM/DD/YYYY).",
        icon: "📆"
      }
    ],
    travel: [
      {
        id: "travel-1",
        title: "Propósito claro",
        tip: "Sé específico sobre el propósito de tu viaje: turismo, negocios, visita familiar, etc.",
        icon: "✈️"
      },
      {
        id: "travel-2",
        title: "Itinerario",
        tip: "Si tienes fechas específicas, inclúyelas. Si no, pon fechas aproximadas.",
        icon: "🗓️"
      }
    ],
    work: [
      {
        id: "work-1",
        title: "Empleo actual",
        tip: "Incluye información detallada de tu empleo actual: empresa, dirección, teléfono.",
        icon: "💼"
      },
      {
        id: "work-2",
        title: "Historial laboral",
        tip: "Lista tus últimos 5 años de empleo. Los huecos grandes pueden generar preguntas.",
        icon: "📊"
      }
    ],
    security: [
      {
        id: "security-1",
        title: "Honestidad total",
        tip: "Responde TODAS las preguntas de seguridad con honestidad. Mentir es causa de rechazo permanente.",
        icon: "⚠️"
      }
    ]
  },
  
  // Tips para simulación de entrevista
  interview: {
    general: [
      {
        id: "interview-1",
        title: "Respuestas cortas",
        tip: "Responde solo lo que te preguntan. No des información extra innecesaria.",
        icon: "💬"
      },
      {
        id: "interview-2",
        title: "Contacto visual",
        tip: "Mantén contacto visual con el oficial consular. Demuestra confianza.",
        icon: "👁️"
      },
      {
        id: "interview-3",
        title: "Documentos organizados",
        tip: "Lleva tus documentos organizados en un folder transparente para fácil acceso.",
        icon: "📂"
      },
      {
        id: "interview-4",
        title: "Lazos con tu país",
        tip: "Enfatiza tus razones para REGRESAR: trabajo, familia, propiedades, estudios.",
        icon: "🏠"
      },
      {
        id: "interview-5",
        title: "No memorices",
        tip: "No memorices respuestas. Suena artificial. Habla naturalmente sobre tu vida.",
        icon: "🗣️"
      }
    ]
  },
  
  // Tips para agendar cita
  appointment: {
    general: [
      {
        id: "appt-1",
        title: "Horario temprano",
        tip: "Las citas temprano en la mañana suelen tener menos espera.",
        icon: "🌅"
      },
      {
        id: "appt-2",
        title: "Llega con tiempo",
        tip: "Llega al menos 30 minutos antes de tu cita. Los retrasos no se perdonan.",
        icon: "⏰"
      },
      {
        id: "appt-3",
        title: "Código de vestimenta",
        tip: "Viste formal pero cómodo. La primera impresión cuenta.",
        icon: "👔"
      }
    ]
  }
};

// Componente de Tips Flotante
export const FloatingTips = ({ context = "documents", subContext = "general", position = "right" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [hasSeenTips, setHasSeenTips] = useState(false);
  
  // Obtener tips del contexto
  const contextTips = CONTEXTUAL_TIPS[context] || {};
  const tips = [
    ...(contextTips.general || []),
    ...(contextTips[subContext] || [])
  ];
  
  useEffect(() => {
    // Mostrar automáticamente después de 2 segundos si no ha visto los tips
    const storageKey = `tips_seen_${context}_${subContext}`;
    const seen = localStorage.getItem(storageKey);
    
    if (!seen && tips.length > 0) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        localStorage.setItem(storageKey, 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [context, subContext, tips.length]);
  
  if (tips.length === 0) return null;
  
  const currentTip = tips[currentTipIndex];
  
  const nextTip = () => {
    setCurrentTipIndex((prev) => (prev + 1) % tips.length);
  };
  
  const prevTip = () => {
    setCurrentTipIndex((prev) => (prev - 1 + tips.length) % tips.length);
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed ${position === "right" ? "right-6" : "left-6"} bottom-24 z-40 
          w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 
          text-white shadow-lg hover:shadow-xl transition-all
          flex items-center justify-center group`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="tips-floating-btn"
      >
        <Lightbulb className="w-6 h-6" />
        {!hasSeenTips && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
        )}
      </motion.button>
      
      {/* Tips Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed ${position === "right" ? "right-6" : "left-6"} bottom-44 z-50
              w-80 bg-white rounded-2xl shadow-2xl border border-navy-100 overflow-hidden`}
            data-testid="tips-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-400 to-amber-500 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-semibold">Tips Útiles</span>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-amber-100 text-sm mt-1">
                {currentTipIndex + 1} de {tips.length}
              </p>
            </div>
            
            {/* Tip Content */}
            <div className="p-5">
              <div className="flex items-start gap-3">
                <span className="text-3xl">{currentTip.icon}</span>
                <div>
                  <h4 className="font-bold text-navy-900 mb-1">{currentTip.title}</h4>
                  <p className="text-navy-600 text-sm leading-relaxed">{currentTip.tip}</p>
                </div>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="border-t border-navy-100 p-3 flex items-center justify-between bg-navy-50">
              <button
                onClick={prevTip}
                className="p-2 hover:bg-navy-100 rounded-lg transition-colors text-navy-600"
                disabled={tips.length <= 1}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Dots */}
              <div className="flex items-center gap-1">
                {tips.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTipIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentTipIndex ? "bg-amber-500" : "bg-navy-300"
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextTip}
                className="p-2 hover:bg-navy-100 rounded-lg transition-colors text-navy-600"
                disabled={tips.length <= 1}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Componente de Tip Inline (para mostrar junto a campos específicos)
export const InlineTip = ({ tipId, context, subContext }) => {
  const contextTips = CONTEXTUAL_TIPS[context] || {};
  const allTips = [...(contextTips.general || []), ...(contextTips[subContext] || [])];
  const tip = allTips.find(t => t.id === tipId);
  
  if (!tip) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-2"
    >
      <span className="text-lg">{tip.icon}</span>
      <div>
        <p className="text-sm font-medium text-amber-800">{tip.title}</p>
        <p className="text-xs text-amber-700">{tip.tip}</p>
      </div>
    </motion.div>
  );
};

// Componente de Tips Banner (para mostrar en la parte superior de una página)
export const TipsBanner = ({ context, subContext = "general" }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  const contextTips = CONTEXTUAL_TIPS[context] || {};
  const tips = contextTips[subContext] || contextTips.general || [];
  
  useEffect(() => {
    if (tips.length > 1) {
      const interval = setInterval(() => {
        setCurrentTipIndex((prev) => (prev + 1) % tips.length);
      }, 8000); // Cambiar cada 8 segundos
      return () => clearInterval(interval);
    }
  }, [tips.length]);
  
  if (tips.length === 0 || !isVisible) return null;
  
  const currentTip = tips[currentTipIndex];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl"
      data-testid="tips-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-xl">{currentTip.icon}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">{currentTip.title}</span>
            </div>
            <p className="text-sm text-amber-700">{currentTip.tip}</p>
          </div>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="p-1 hover:bg-amber-100 rounded-full transition-colors text-amber-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Progress dots */}
      {tips.length > 1 && (
        <div className="flex items-center justify-center gap-1 mt-3">
          {tips.map((_, idx) => (
            <div
              key={idx}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                idx === currentTipIndex ? "bg-amber-500" : "bg-amber-300"
              }`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default FloatingTips;
